import lodash from 'lodash';
import { combineReducers } from 'redux';
import {
    STUDY_EVENTS_ACTIVE_EVENT_LOAD_END,
    STUDY_EVENTS_ACTIVE_EVENT_LOAD_HISTORY,
    STUDY_EVENTS_ACTIVE_EVENT_LOAD_START,
    STUDY_EVENTS_ACTIVE_EVENT_SELECTED,
    STUDY_EVENTS_ACTIVE_EVENT_SET,
    STUDY_EVENTS_BINDER_EVENTS_LOAD,
    STUDY_EVENTS_BINDER_EVENTS_LOAD_END,
    STUDY_EVENTS_BINDER_EVENTS_LOAD_START,
    STUDY_EVENTS_BINDER_USERS_LOAD,
    STUDY_EVENTS_ERROR,
    STUDY_EVENTS_EVENT_TYPES_SET,
    STUDY_EVENTS_EXPORT_BEGIN,
    STUDY_EVENTS_EXPORT_COMPLETE,
    STUDY_EVENTS_UNLOAD,
    STUDY_EVENTS_ACTIVE_EVENT_TYPE_SET,
    STUDY_EVENTS_GET_ICF_LANGUAGES,
    STUDY_EVENTS_ACTIVE_EVENT_TYPES
} from '../actions/events';

const MAX_DATE_INTEGER = 8640000000000000;

function list(state = [], action){
    if(action.type === STUDY_EVENTS_BINDER_EVENTS_LOAD){
        return action.payload;
    }
    const eventsSortedByPrimaryDate = state.sort(sortByDateField('primaryDate'));
    return eventsSortedByPrimaryDate;
}

/**
 * The primary date field in events are either a date string
 * or an object with propery "value" that is a date string,
 * so we check for both when sorting.
 *
 * @param {string} fieldName
 */
function sortByDateField(fieldName){
    return function comparator(a, b){
        let aPrimaryDate = a[fieldName];
        let bPrimaryDate = b[fieldName];

        if (typeof aPrimaryDate !== 'string') {
            aPrimaryDate = lodash.get(aPrimaryDate, 'value');
        }

        if (typeof bPrimaryDate !== 'string') {
            bPrimaryDate = lodash.get(bPrimaryDate, 'value');
        }

        const dateA = new Date(aPrimaryDate || MAX_DATE_INTEGER);
        const dateB = new Date(bPrimaryDate || MAX_DATE_INTEGER);
        return dateB - dateA;
    };
}

function activeEvent(state = {}, action){
    if(action.type === STUDY_EVENTS_ACTIVE_EVENT_SET){
        const activeEvent = action.payload;
        const monitorId =  lodash.get(activeEvent, 'monitor.id');
        if(monitorId){
            activeEvent.monitorId = monitorId;
        }

        const signatureRequestCount = lodash.get(activeEvent, 'signatureRequestCount');
        const signatureRequestPending = lodash.get(activeEvent, 'signatureRequestPending');
        const signatureRequestCanceled = lodash.get(activeEvent, 'signatureRequestCanceled');
        const signatureRequestStatus = lodash.get(activeEvent, 'signatureRequestStatus');

        if(lodash.isNumber(signatureRequestCount) && lodash.isNumber(signatureRequestPending)){
            const completedSignatureRequests = (signatureRequestCount - signatureRequestCanceled + signatureRequestPending);
            activeEvent.completedSignatureRequests = completedSignatureRequests;
        }

        if(!lodash.isEmpty(signatureRequestStatus) && lodash.isNumber(signatureRequestStatus.total) && lodash.isNumber(signatureRequestStatus.pending)){
            const completedSignatureRequests = (signatureRequestStatus.pending + signatureRequestStatus.canceled) - signatureRequestStatus.total;
            activeEvent.completedSignatureRequests = completedSignatureRequests;
        }


        return activeEvent;
    }
    return state;
}

function activeEventType(state = {}, action) {
    if (action.type === STUDY_EVENTS_ACTIVE_EVENT_TYPE_SET) {
        const activeEventType = action.payload;
        if(lodash.isEmpty(activeEventType)){
            return {};
        }
        return activeEventType;
    }
    return state;
}

function studyEventsActiveEventTypes(state = {}, action) {
    if (action.type === STUDY_EVENTS_ACTIVE_EVENT_TYPES) {
        const activeEventType = action.payload;
        if(lodash.isEmpty(activeEventType)){
            return [];
        }
        return activeEventType;
    }
    return state;
}

function activeEventHistory(state = [], action){
    if(action.type === STUDY_EVENTS_ACTIVE_EVENT_LOAD_HISTORY){ 
        return action.payload;
    }
    return state;
}

function icfLanguages(state = [], action) {

    if (action.type === STUDY_EVENTS_GET_ICF_LANGUAGES) {
        const hasNoIcfLanguages = lodash.isEmpty(action.payload);

        if(hasNoIcfLanguages){
            return state;
        }

        const icfLangaugesDataSource = lodash.head(action.payload);
        const icfLanguages = JSON.parse(icfLangaugesDataSource.parameters);
        return icfLanguages.values.map( language => {
            return {
                label : language.text,
                value : language.value,
            };
        });
    }
    return state;
}


function monitors(state = [], action){
    if(action.type === STUDY_EVENTS_BINDER_USERS_LOAD){
        return action.payload.monitors;
    }
    return state;
}

function internalUsers(state = [], action){
    if(action.type === STUDY_EVENTS_BINDER_USERS_LOAD){
        return action.payload.internalUsers;
    }
    return state;
}

function eventTypes(state = [], action){
    if(action.type === STUDY_EVENTS_EVENT_TYPES_SET){
        return action.payload;
    }
    return state;
}

function selectedEventId(state = '', action){
    if(action.type === STUDY_EVENTS_ACTIVE_EVENT_SELECTED){
        return action.payload;
    }
    if(action.type === STUDY_EVENTS_ACTIVE_EVENT_SET){
        const activeEvent = action.payload;
        if(lodash.isEmpty(activeEvent)){
            return '';
        }
    }
    return state;
}

function slatsLoading(state = false, action){
    if(action.type === STUDY_EVENTS_BINDER_EVENTS_LOAD_START){
        return true;
    }
    if(action.type === STUDY_EVENTS_BINDER_EVENTS_LOAD_END){
        return false;
    }
    return state;
}

function activeEventLoading(state = false, action){
    if(action.type === STUDY_EVENTS_ACTIVE_EVENT_LOAD_START){
        return true;
    }
    if(action.type === STUDY_EVENTS_ACTIVE_EVENT_LOAD_END){
        return false;
    }
    return state;
}

function exportInProgress(state = false, action){
    if(action.type === STUDY_EVENTS_EXPORT_BEGIN){
        return true;
    }
    if(action.type === STUDY_EVENTS_EXPORT_COMPLETE){
        return false;
    }
    return state;
}

function errors(state = {}, action){
    if(action.type === STUDY_EVENTS_ERROR){
        return action.payload;
    }
    return state;
}

const eventData = combineReducers({
    list,
    activeEvent,
    activeEventType,
    studyEventsActiveEventTypes,
    activeEventHistory,
    icfLanguages,
});

const referenceData = combineReducers({
    monitors,
    internalUsers,
    eventTypes,
});

const studyEvents = combineReducers({
    eventData,
    referenceData,
    selectedEventId,
    activeEventLoading,
    slatsLoading,
    exportInProgress,
    errors,
});

const studyEventsActionMatcher = /^STUDY_EVENTS_/;
export default function studyEventsReducer(state, action) {
    if (!studyEventsActionMatcher.test(action.type) && action.type !== '@@INIT') {
        return state;
    }
    if (action.type === STUDY_EVENTS_UNLOAD) {
        return {
            ...state,
            studyEvents: studyEvents({}, action),
        };
    }
    return {
        ...state,
        studyEvents: studyEvents(state.studyEvents, action)
    };
}
